import { platformStorageSha256Schema, platformStorageUploadIntentIdSchema, platformStorageUploadTransportSchema, } from '@crest/platform-contracts/storage';
import { z } from 'zod';
export const mitigationMoistureEvidenceCapability = 'mitigation.moisture-evidence.v1';
export const mitigationMoistureEvidenceContractVersion = '1.0.0';
export const mitigationMoistureEvidenceQueueSchemaVersion = 1;
// Core-only x.y.z, matching the database's client_version check exactly so an
// API-accepted client version can never fail at insert.
export const mitigationMoistureEvidenceClientVersionSchema = z
    .string()
    .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/);
export const mitigationMoistureEvidenceIdSchema = z.string().uuid();
export const mitigationMoistureEvidenceStateSchema = z.enum([
    'pending-upload',
    'complete',
    'quarantined',
]);
export const mitigationMoistureReadingScaleSchema = z.enum(['percent', 'relative']);
export const mitigationMoistureEvidenceContentTypeSchema = z.enum([
    'image/jpeg',
    'image/png',
    'image/heic',
]);
export const mitigationMoistureEvidenceFileSchema = z.strictObject({
    originalFileName: z.string().trim().min(1).max(240).refine((value) => !/[\\/\u0000-\u001f\u007f]/.test(value) && value !== '.' && value !== '..', 'Evidence file names cannot contain path separators or control characters.'),
    contentType: mitigationMoistureEvidenceContentTypeSchema,
    sizeBytes: z.number().int().positive().max(100 * 1_024 * 1_024),
    sha256: platformStorageSha256Schema,
});
export const mitigationMoistureEvidenceSchema = z.strictObject({
    id: mitigationMoistureEvidenceIdSchema,
    tenantId: z.string().uuid(),
    projectId: z.string().uuid(),
    commandId: z.string().uuid(),
    // A client-reported claim recorded for diagnostics and compatibility only;
    // it is never authorization and never proof the evidence came from mobile.
    clientSurface: z.enum(['web', 'mobile']),
    clientVersion: mitigationMoistureEvidenceClientVersionSchema,
    queueItemId: z.string().uuid().nullable(),
    state: mitigationMoistureEvidenceStateSchema,
    locationLabel: z.string().trim().min(1).max(160),
    materialLabel: z.string().trim().min(1).max(160),
    readingScale: mitigationMoistureReadingScaleSchema,
    readingValueMillis: z.number().int().nonnegative().max(999_000),
    meterName: z.string().trim().min(1).max(160).nullable(),
    meterSerial: z.string().trim().min(1).max(160).nullable(),
    notes: z.string().trim().min(1).max(2_000).nullable(),
    deviceOccurredAt: z.string().datetime({ offset: true }),
    serverReceivedAt: z.string().datetime({ offset: true }),
    mediaCapturedAt: z.string().datetime({ offset: true }).nullable(),
    uploadIntentId: platformStorageUploadIntentIdSchema,
    storageObjectId: z.string().uuid(),
    file: mitigationMoistureEvidenceFileSchema,
    mediaLatitude: z.number().min(-90).max(90).nullable(),
    mediaLongitude: z.number().min(-180).max(180).nullable(),
    mediaAccuracyMeters: z.number().nonnegative().max(100_000).nullable(),
    createdByMembershipId: z.string().uuid(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    revision: z.number().int().positive(),
});
export const mitigationMoistureEvidenceClientSchema = z.strictObject({
    surface: z.enum(['web', 'mobile']),
    version: mitigationMoistureEvidenceClientVersionSchema,
    contractVersion: z.literal(mitigationMoistureEvidenceContractVersion),
    queueSchemaVersion: z.literal(mitigationMoistureEvidenceQueueSchemaVersion),
    queueItemId: z.string().uuid().nullable(),
});
export const mitigationInitiateMoistureEvidenceUploadSchema = z.strictObject({
    commandId: z.string().uuid(),
    client: mitigationMoistureEvidenceClientSchema,
    locationLabel: z.string().trim().min(1).max(160),
    materialLabel: z.string().trim().min(1).max(160),
    readingScale: mitigationMoistureReadingScaleSchema,
    readingValueMillis: z.number().int().nonnegative().max(999_000),
    meterName: z.string().trim().min(1).max(160).nullable().default(null),
    meterSerial: z.string().trim().min(1).max(160).nullable().default(null),
    notes: z.string().trim().min(1).max(2_000).nullable().default(null),
    deviceOccurredAt: z.string().datetime({ offset: true }),
    file: mitigationMoistureEvidenceFileSchema,
}).superRefine((value, context) => {
    if (value.readingScale === 'percent' && value.readingValueMillis > 100_000) {
        context.addIssue({
            code: 'custom',
            path: ['readingValueMillis'],
            message: 'Percent moisture readings cannot exceed 100.000 percent.',
        });
    }
});
export const mitigationMoistureEvidenceUploadRequiredSchema = z.strictObject({
    status: z.literal('upload-required'),
    evidence: mitigationMoistureEvidenceSchema,
    uploadIntentId: platformStorageUploadIntentIdSchema,
    transport: platformStorageUploadTransportSchema,
});
export const mitigationMoistureEvidenceAlreadyCompleteSchema = z.strictObject({
    status: z.literal('already-complete'),
    evidence: mitigationMoistureEvidenceSchema,
});
export const mitigationMoistureEvidenceUploadResponseSchema = z.strictObject({
    data: z.discriminatedUnion('status', [
        mitigationMoistureEvidenceUploadRequiredSchema,
        mitigationMoistureEvidenceAlreadyCompleteSchema,
    ]),
});
export const mitigationReauthorizeMoistureEvidenceUploadSchema = z.strictObject({
    client: mitigationMoistureEvidenceClientSchema,
});
export const mitigationFinalizeMoistureEvidenceUploadSchema = z.strictObject({
    uploadIntentId: platformStorageUploadIntentIdSchema,
    client: mitigationMoistureEvidenceClientSchema,
});
export const mitigationMoistureEvidenceResponseSchema = z.strictObject({
    data: mitigationMoistureEvidenceSchema,
});
export const mitigationMoistureEvidenceListQuerySchema = z.strictObject({
    limit: z.number().int().min(1).max(100).default(30),
    offset: z.number().int().min(0).max(1_000_000).default(0),
    state: z.enum(['visible', 'complete', 'recoverable']).default('visible'),
});
export const mitigationMoistureEvidenceListResponseSchema = z.strictObject({
    data: z.array(mitigationMoistureEvidenceSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
});
//# sourceMappingURL=mitigation-moisture-evidence.contract.js.map